import { z } from 'zod'
import { OptionalDataValidationSchema } from './ForwardingTaskDataValidationSchema'
import { OptionalClinicalAssistanceSchema } from './ForwardingTaskClinicalAssistanceSchema'

const ContactSchema = z.object({
	contact: z
		.object({
			contactVictim: z.boolean().optional(),
			successfulContact: z
				.string()
				.transform((val) => parseInt(val))
				.optional(),
			authorizeRecording: z.boolean().optional(),
			contactFailedReason: z.number().optional(),
			contactFailedOtherReason: z.string().min(4).optional(),
		})
		.optional()
		.superRefine((data, ctx) => {
			// Only validate the phone-contact answers once "contactVictim" has
			// been answered. When only the initial form is filled this stays
			// undefined and the section must not block submission.
			if (data?.contactVictim === undefined) return
			if (data.contactVictim) {
				if (data.successfulContact === undefined) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Required',
						path: ['contact', 'successfulContact'],
					})
				}
				if (
					data.successfulContact === 1 &&
					data.authorizeRecording === undefined
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Required',
						path: ['contact', 'authorizeRecording'],
					})
				}
			} else {
				if (data.contactFailedReason === undefined) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Required',
						path: ['contact', 'contactFailedReason'],
					})
				}
				if (
					data.contactFailedReason === 4 &&
					data.contactFailedOtherReason === undefined
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Required',
						path: ['contact', 'contactFailedOtherReason'],
					})
				}
			}
		}),
})

const BodyPartSchema = z.object({
	bodyPart: z
		.object({
			bodyPartName: z
				.string({ invalid_type_error: 'Required' })
				.optional()
				.transform((val) => (val ? parseInt(val, 10) : undefined)),
		})
		.optional(),
})

const NetworkFollowSchema = z.object({
	networkFollow: z
		.object({
			followOnTrustNetwork: z.boolean(),
			notFollowOnTrustNetworkReason: z.number().optional(),
			notFollowOnTrustNetworkOtherReason: z.string().optional(),
		})
		.optional(),
})

const providerForwardingSchema = z.object({
	provider: z
		.object({
			establishmentId: z
				.string()
				.transform((val) => parseInt(val))
				.optional(),
			temporaryEstablishmentId: z
				.string()
				.transform((val) => parseInt(val))
				.optional(),
			alreadyBeingAssisted: z.boolean().optional(),
		})
		.optional(),
})

// Mobile phone collected from the operator when the client has no registered
// mobile. It lives in its own namespace so it never leaks into the forwarding
// payload — it is used to update the client data before submitting.
const ClientPhoneNumberSchema = z.object({
	client: z
		.object({
			phoneNumber: z.union([z.string(), z.number()]).optional(),
		})
		.optional(),
})

const StrictTaskSchema = (
	isAllianzInsuranceCompany,
	accidentType,
	hasClientPhone
) =>
	z
		.object({
			observations: z.string().optional(),
			processId: z.number().optional(),
			injuryDescription: z.string().optional(),
			injuryType: z
				.string()
				.optional()
				.transform((val) => (val ? parseInt(val, 10) : undefined)),
		})
		.merge(ContactSchema)
		.merge(OptionalDataValidationSchema)
		.merge(BodyPartSchema)
		.merge(OptionalClinicalAssistanceSchema)
		.merge(NetworkFollowSchema)
		.merge(providerForwardingSchema)
		.merge(ClientPhoneNumberSchema)

export const ForwardingTaskSchema = (
	isAllianzInsuranceCompany,
	accidentType,
	hasClientPhone
) => {
	const strictSchema = StrictTaskSchema(
		isAllianzInsuranceCompany,
		accidentType,
		hasClientPhone
	)

	return strictSchema.superRefine((data, ctx) => {
		// When the client has no registered mobile phone the operator must
		// provide one — submitting forwards a task whose SMS notification would
		// otherwise never reach the client.
		if (!hasClientPhone) {
			const phoneNumber = data?.client?.phoneNumber
			if (
				phoneNumber === undefined ||
				phoneNumber === null ||
				String(phoneNumber).trim() === ''
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Required',
					path: ['client', 'phoneNumber'],
				})
			} else if (!/^9\d{8}$/.test(String(phoneNumber))) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Invalid phone number',
					path: ['client', 'phoneNumber'],
				})
			}
		}

		if (data?.contact?.successfulContact === 1) {
			if (
				data?.contact?.contactVictim === false ||
				data?.clinicalAssistance?.InjuredHasClinicalAssistance ===
					false ||
				(data?.clinicalAssistance?.InjuredHasClinicalAssistance ===
					true &&
					data?.clinicalAssistance?.injuredHospitalized === false)
			) {
				if (!data?.networkFollow?.followOnTrustNetwork) {
					if (
						data?.networkFollow?.notFollowOnTrustNetworkReason ===
						undefined
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Required',
							path: [
								'networkFollow',
								'notFollowOnTrustNetworkReason',
							],
						})
					}

					if (
						data?.networkFollow?.notFollowOnTrustNetworkReason ===
							3 &&
						data?.networkFollow
							?.notFollowOnTrustNetworkOtherReason === undefined
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Required',
							path: [
								'networkFollow',
								'notFollowOnTrustNetworkOtherReason',
							],
						})
					}
				}
			}

			if (
				(data?.networkFollow?.followOnTrustNetwork !== undefined &&
					data.networkFollow.followOnTrustNetwork === true) ||
				(data?.clinicalAssistance?.injuredHospitalized !== undefined &&
					data.clinicalAssistance.injuredHospitalized === true)
			) {
				if (data?.clinicalAssistance?.injuredHospitalized) {
					// if (
					// 	data?.provider?.temporaryEstablishmentId ===
					// 	undefined
					// ) {
					// 	ctx.addIssue({
					// 		code: z.ZodIssueCode.custom,
					// 		message: 'Required',
					// 		path: ['provider', 'temporaryEstablishmentId'],
					// 	})
					// }
				} else {
					if (data?.provider?.establishmentId === undefined) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Required',
							path: ['provider', 'establishmentId'],
						})
					}
				}
			}

			// Provider is only required when the injured is followed within the
			// Trust network. When not following (any reason, including
			// hospitalization / treatment outside network) no provider is asked.
			if (
				data?.networkFollow?.followOnTrustNetwork === true &&
				accidentType !== 'AD'
			) {
				if (data?.provider?.establishmentId === undefined) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Required',
						path: ['provider', 'establishmentId'],
					})
				}
			}

			if (
				data?.contact?.successfulContact === 1 &&
				data?.dataValidation?.postalCode === undefined
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Required',
					path: ['dataValidation', 'postalCode'],
				})
			}
		}

		if (
			data?.contact?.contactVictim === undefined &&
			data?.provider?.establishmentId === undefined
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Required',
				path: ['provider', 'establishmentId'],
			})
		}
	})
}
